import { useSelector } from 'react-redux';
import { allowFormObjectAddOrRemove, formObjectFieldsState } from '@/forms/objects/helper';
import {
  formSections,
  safeFormObjects,
  unknownFields,
  unknownFieldValues,
} from '@/forms/state/state';
import { readStates, saveStates, states } from '@/constants/lifecycle';
import { formDisplayFields } from '@/forms/helper';
import findResult from '@/utils/resultsHelper';
import { workspaceForms } from '@/workspaces/constants';
import { resolveRequestForm, resolveRequestWorkflow } from '../../state/state';

const useRequestFieldList = ({ category, code, section }) => useSelector(({
  requests: { requests },
  workflows: { workflows },
  workspaces: { forms },
}) => {
  const { request, sections } = resolveRequestForm(requests, workflows, forms, { category, code });
  return { request, fields: formDisplayFields(sections[section].fields) };
});

const useFormFields = ({ category, code }) => useSelector(({
  auth: { role: { permissions: { requests: { create } } } },
  requests: { requests },
  workflows: { workflows },
}) => {
  const { request } = resolveRequestWorkflow(requests, workflows, { category, code }, {
    isNew: false,
    ...saveStates,
  });

  return {
    canEdit: create && request.isNew,
    request,
    saveErrors: request.saveErrors,
    saveStatus: request.saveStatus,
  };
});

const useRequestObjectFields = () => useSelector(({
  auth: { role: { permissions: { requests: { create } } } },
  requests: { requests, requestFieldItems },
  workflows: { workflows },
  workspaces: { forms },
}) => {
  const { request, sections } = resolveRequestForm(requests, workflows, forms, requestFieldItems, { isNew: false });
  const form = sections ? { sections } : formSections(['about']);

  return {
    canEdit: create && request.isNew,
    ...requestFieldItems,
    ...formObjectFieldsState(form, requestFieldItems),
    sections,
  };
});

const useRequestLinksList = ({ category, code }) => useSelector(({
  auth: { role: { permissions: { requests: { create } } } },
  requests: { requests },
  workflows: { workflows },
  workspaces: { forms },
}) => {
  const { request, sections } = resolveRequestForm(requests, workflows, forms, { category, code });
  return { canEdit: create && request.isNew, sections };
});

const useRequestObjectsTable = ({
  category,
  code,
  field,
  items,
}) => useSelector(({
  auth: { role: { permissions: { requests: { create } } } },
  requests: { requests },
  workflows: { workflows },
  workspaces: { forms },
}) => {
  const { request, sections } = resolveRequestForm(requests, workflows, forms, { category, code }, { isNew: false });

  return {
    ...allowFormObjectAddOrRemove({ field }, safeFormObjects(items)),
    canEdit: request.isNew && create,
    sections,
  };
});

const useRequestFormDialogs = () => {
  const { role: { permissions: { requests: { read } } } } = useSelector(({ auth }) => auth);
  const { requestFieldItems: { linksOpen, open } } = useSelector(({ requests }) => requests);
  return { allowLinks: linksOpen && read, allowObjects: open && read };
};

const useRequestUnknownFields = ({ category, code }) => useSelector(({
  requests: { requests },
  workflows: { workflows },
  workspaces: { forms },
}) => {
  const { request, workflow } = resolveRequestWorkflow(requests, workflows, { category, code }, readStates);
  const { readStatus, sections } = findResult(forms, 'open', workspaceForms.REQUEST);
  const workspaceSections = request.workspace_requested ? sections : {};
  const checkUnknown = !request.workspace_requested || ([states.ERROR, states.SUCCESS].includes(readStatus));
  const values = checkUnknown ? unknownFieldValues(request, { ...workflow.sections, ...workspaceSections }) : {};

  return { fields: unknownFields(values), request };
});

export default {
  useFormFields,
  useRequestFieldList,
  useRequestFormDialogs,
  useRequestLinksList,
  useRequestObjectFields,
  useRequestObjectsTable,
  useRequestUnknownFields,
};
